import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { nanoid } from 'nanoid';
import type {
  LeadFilterState,
  SavedSearchPreset,
  LeadSortBy,
  SortOrder,
  LicenseStatus,
  MarketStatus,
} from '../types/leads';
import {
  DEFAULT_LEAD_FILTERS,
  URL_PARAM_MAP,
} from '../types/leads';
import { useMarketStore } from '../store/useMarketStore';

const SAVED_SEARCHES_KEY = 'lead-pipeline-saved-searches';

/**
 * Serialize filter state to URL search params
 */
function serializeToParams(
  filters: LeadFilterState,
  defaults: LeadFilterState
): URLSearchParams {
  const params = new URLSearchParams();

  // Only include non-default values
  if (filters.tiers.length > 0) {
    params.set(URL_PARAM_MAP.tiers, filters.tiers.join(','));
  }

  if (filters.zipCodes.length > 0) {
    params.set(URL_PARAM_MAP.zipCodes, filters.zipCodes.join(','));
  }

  if (filters.unitMin !== null) {
    params.set(URL_PARAM_MAP.unitMin, String(filters.unitMin));
  }

  if (filters.unitMax !== null) {
    params.set(URL_PARAM_MAP.unitMax, String(filters.unitMax));
  }

  if (filters.scoreMin !== null) {
    params.set(URL_PARAM_MAP.scoreMin, String(filters.scoreMin));
  }

  if (filters.scoreMax !== null) {
    params.set(URL_PARAM_MAP.scoreMax, String(filters.scoreMax));
  }

  if (filters.licenseStatus !== defaults.licenseStatus) {
    params.set(URL_PARAM_MAP.licenseStatus, filters.licenseStatus);
  }

  if (filters.marketStatus !== defaults.marketStatus) {
    params.set(URL_PARAM_MAP.marketStatus, filters.marketStatus);
  }

  if (filters.dateFrom !== null) {
    params.set(URL_PARAM_MAP.dateFrom, filters.dateFrom);
  }

  if (filters.dateTo !== null) {
    params.set(URL_PARAM_MAP.dateTo, filters.dateTo);
  }

  if (filters.sortBy !== defaults.sortBy) {
    params.set(URL_PARAM_MAP.sortBy, filters.sortBy);
  }

  if (filters.sortOrder !== defaults.sortOrder) {
    params.set(URL_PARAM_MAP.sortOrder, filters.sortOrder);
  }

  if (filters.page !== defaults.page) {
    params.set(URL_PARAM_MAP.page, String(filters.page));
  }

  if (filters.limit !== defaults.limit) {
    params.set(URL_PARAM_MAP.limit, String(filters.limit));
  }

  return params;
}

/**
 * Parse URL search params to filter state
 */
function parseFromParams(
  params: URLSearchParams,
  defaults: LeadFilterState
): LeadFilterState {
  const filters = { ...defaults };

  // Parse array fields
  const tiersParam = params.get(URL_PARAM_MAP.tiers);
  if (tiersParam) {
    filters.tiers = tiersParam.split(',').filter(Boolean);
  }

  const zipCodesParam = params.get(URL_PARAM_MAP.zipCodes);
  if (zipCodesParam) {
    filters.zipCodes = zipCodesParam.split(',').filter(Boolean);
  }

  // Parse numeric fields
  const unitMinParam = params.get(URL_PARAM_MAP.unitMin);
  if (unitMinParam) {
    const parsed = parseInt(unitMinParam, 10);
    if (!isNaN(parsed)) filters.unitMin = parsed;
  }

  const unitMaxParam = params.get(URL_PARAM_MAP.unitMax);
  if (unitMaxParam) {
    const parsed = parseInt(unitMaxParam, 10);
    if (!isNaN(parsed)) filters.unitMax = parsed;
  }

  const scoreMinParam = params.get(URL_PARAM_MAP.scoreMin);
  if (scoreMinParam) {
    const parsed = parseInt(scoreMinParam, 10);
    if (!isNaN(parsed)) filters.scoreMin = parsed;
  }

  const scoreMaxParam = params.get(URL_PARAM_MAP.scoreMax);
  if (scoreMaxParam) {
    const parsed = parseInt(scoreMaxParam, 10);
    if (!isNaN(parsed)) filters.scoreMax = parsed;
  }

  // Parse enum fields
  const licenseStatusParam = params.get(URL_PARAM_MAP.licenseStatus);
  if (licenseStatusParam && ['any', 'licensed', 'unlicensed'].includes(licenseStatusParam)) {
    filters.licenseStatus = licenseStatusParam as LicenseStatus;
  }

  const marketStatusParam = params.get(URL_PARAM_MAP.marketStatus);
  if (marketStatusParam && ['any', 'on-market', 'off-market'].includes(marketStatusParam)) {
    filters.marketStatus = marketStatusParam as MarketStatus;
  }

  // Parse date fields
  const dateFromParam = params.get(URL_PARAM_MAP.dateFrom);
  if (dateFromParam) {
    filters.dateFrom = dateFromParam;
  }

  const dateToParam = params.get(URL_PARAM_MAP.dateTo);
  if (dateToParam) {
    filters.dateTo = dateToParam;
  }

  // Parse sort fields
  const sortByParam = params.get(URL_PARAM_MAP.sortBy);
  if (sortByParam) {
    filters.sortBy = sortByParam as LeadSortBy;
  }

  const sortOrderParam = params.get(URL_PARAM_MAP.sortOrder);
  if (sortOrderParam && ['asc', 'desc'].includes(sortOrderParam)) {
    filters.sortOrder = sortOrderParam as SortOrder;
  }

  // Parse pagination
  const pageParam = params.get(URL_PARAM_MAP.page);
  if (pageParam) {
    const parsed = parseInt(pageParam, 10);
    if (!isNaN(parsed) && parsed > 0) filters.page = parsed;
  }

  const limitParam = params.get(URL_PARAM_MAP.limit);
  if (limitParam) {
    const parsed = parseInt(limitParam, 10);
    if (!isNaN(parsed) && parsed > 0) filters.limit = parsed;
  }

  return filters;
}

/**
 * Build API query string from filter state + active market
 */
function buildApiQueryString(filters: LeadFilterState, marketId: string): string {
  const params = new URLSearchParams();

  // Market filter — always include so the backend scopes results correctly
  params.set('market_id', marketId);

  // Tiers
  if (filters.tiers.length > 0) {
    filters.tiers.forEach((tier) => params.append('tier', tier));
  }

  // ZIP codes
  if (filters.zipCodes.length > 0) {
    filters.zipCodes.forEach((zip) => params.append('zip_code', zip));
  }

  // Unit range
  if (filters.unitMin !== null) {
    params.set('unit_min', String(filters.unitMin));
  }
  if (filters.unitMax !== null) {
    params.set('unit_max', String(filters.unitMax));
  }

  // Score range
  if (filters.scoreMin !== null) {
    params.set('score_min', String(filters.scoreMin));
  }
  if (filters.scoreMax !== null) {
    params.set('score_max', String(filters.scoreMax));
  }

  // License status
  if (filters.licenseStatus !== 'any') {
    params.set('has_rental_license', filters.licenseStatus === 'licensed' ? 'true' : 'false');
  }

  // Market status
  if (filters.marketStatus !== 'any') {
    params.set('listing_status', filters.marketStatus === 'on-market' ? 'active' : 'off-market');
  }

  // Date range
  if (filters.dateFrom) {
    params.set('date_from', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('date_to', filters.dateTo);
  }

  // Sorting
  params.set('sort_by', filters.sortBy);
  params.set('sort_order', filters.sortOrder);

  // Pagination
  params.set('offset', String((filters.page - 1) * filters.limit));
  params.set('limit', String(filters.limit));

  return params.toString();
}

/**
 * Load saved searches from localStorage
 */
function loadSavedSearches(): SavedSearchPreset[] {
  try {
    const stored = localStorage.getItem(SAVED_SEARCHES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load saved searches:', error);
  }
  return [];
}

/**
 * Save searches to localStorage
 */
function persistSavedSearches(searches: SavedSearchPreset[]): void {
  try {
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(searches));
  } catch (error) {
    console.error('Failed to save searches:', error);
  }
}

/**
 * Custom hook for managing lead pipeline filters with URL sync and saved presets
 */
export function useLeadFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [savedSearches, setSavedSearches] = useState<SavedSearchPreset[]>(() =>
    loadSavedSearches()
  );

  // Subscribe to active market — changing the market auto-refreshes the leads list
  const activeMarket = useMarketStore((s) => s.activeMarket);

  // Parse current filters from URL
  const filters = useMemo(
    () => parseFromParams(searchParams, DEFAULT_LEAD_FILTERS),
    [searchParams]
  );

  // Check if any filters are active (different from defaults)
  const hasActiveFilters = useMemo(() => {
    return (
      filters.tiers.length > 0 ||
      filters.zipCodes.length > 0 ||
      filters.unitMin !== null ||
      filters.unitMax !== null ||
      filters.scoreMin !== null ||
      filters.scoreMax !== null ||
      filters.licenseStatus !== DEFAULT_LEAD_FILTERS.licenseStatus ||
      filters.marketStatus !== DEFAULT_LEAD_FILTERS.marketStatus ||
      filters.dateFrom !== null ||
      filters.dateTo !== null
    );
  }, [filters]);

  // Generate API query string — scoped to the active market
  const apiQueryString = useMemo(
    () => buildApiQueryString(filters, activeMarket.market_id),
    [filters, activeMarket.market_id]
  );

  // Update filters and sync to URL
  const setFilters = useCallback(
    (newFilters: Partial<LeadFilterState>) => {
      const updatedFilters = { ...filters, ...newFilters };
      // Reset to page 1 when filters change (except for page changes)
      if (!('page' in newFilters)) {
        updatedFilters.page = 1;
      }
      const params = serializeToParams(updatedFilters, DEFAULT_LEAD_FILTERS);
      setSearchParams(params, { replace: true });
    },
    [filters, setSearchParams]
  );

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Save current filters as a preset
  const savePreset = useCallback(
    (name: string) => {
      const preset: SavedSearchPreset = {
        id: nanoid(),
        name,
        filters: { ...filters },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const updated = [...savedSearches, preset];
      setSavedSearches(updated);
      persistSavedSearches(updated);
      return preset;
    },
    [filters, savedSearches]
  );

  // Load a saved preset
  const loadPreset = useCallback(
    (presetId: string) => {
      const preset = savedSearches.find((s) => s.id === presetId);
      if (preset) {
        const mergedFilters = { ...DEFAULT_LEAD_FILTERS, ...preset.filters };
        const params = serializeToParams(mergedFilters, DEFAULT_LEAD_FILTERS);
        setSearchParams(params, { replace: true });
      }
    },
    [savedSearches, setSearchParams]
  );

  // Delete a saved preset
  const deletePreset = useCallback(
    (presetId: string) => {
      const updated = savedSearches.filter((s) => s.id !== presetId);
      setSavedSearches(updated);
      persistSavedSearches(updated);
    },
    [savedSearches]
  );

  // Update preset
  const updatePreset = useCallback(
    (presetId: string, updates: Partial<Pick<SavedSearchPreset, 'name' | 'filters'>>) => {
      const updated = savedSearches.map((s) =>
        s.id === presetId
          ? { ...s, ...updates, updatedAt: new Date().toISOString() }
          : s
      );
      setSavedSearches(updated);
      persistSavedSearches(updated);
    },
    [savedSearches]
  );

  return {
    // Current filter state
    filters,

    // Computed values
    hasActiveFilters,
    apiQueryString,

    // Filter actions
    setFilters,
    resetFilters,

    // Saved search management
    savedSearches,
    savePreset,
    loadPreset,
    deletePreset,
    updatePreset,
  };
}

// Export type for the hook return value
export type UseLeadFiltersReturn = ReturnType<typeof useLeadFilters>;
